import { useState, useCallback } from "react"
import { v4 as uuidv4 } from "uuid"
import { BadgeElement, BadgeTemplate } from "sharedTypes/badge"

/**
 * Manages clipboard operations (copy, paste, cut)
 */
const useClipboard = (
  currentTemplate: BadgeTemplate | undefined,
  selectedElementIds: string[],
  onElementsAdded: (elements: BadgeElement[]) => void,
  onElementsDeleted: () => void,
) => {
  const [copiedElements, setCopiedElements] = useState<BadgeElement[]>([])
  // A cut is pasted back where it was; a copy 10 mm down-right, so it can be seen
  const [pasteOffset, setPasteOffset] = useState(10)

  const copy = useCallback((offset: number) => {
    if (!currentTemplate || selectedElementIds.length === 0) return

    const elementsToCopy = currentTemplate.badgeData.elements.filter(
      (el) => selectedElementIds.includes(el.id),
    )
    if (elementsToCopy.length > 0) {
      setCopiedElements(elementsToCopy)
      setPasteOffset(offset)
    }
  }, [currentTemplate, selectedElementIds])

  const handleCopyElement = useCallback(() => copy(10), [copy])

  const handlePasteElement = useCallback(() => {
    if (!currentTemplate || copiedElements.length === 0) return

    // Find the maximum z-index to place pasted elements on top
    const maxZIndex = Math.max(
      ...currentTemplate.badgeData.elements.map((el) => el.zIndex),
      0,
    )

    // Create new elements based on the copied ones
    const newElements = copiedElements.map((copiedElement, index) => ({
      ...copiedElement,
      id: uuidv4(),
      x: copiedElement.x + pasteOffset,
      y: copiedElement.y + pasteOffset,
      zIndex: maxZIndex + 1 + index,
    }))

    onElementsAdded(newElements)
  }, [currentTemplate, copiedElements, pasteOffset, onElementsAdded])

  const handleCutElement = useCallback(() => {
    if (!currentTemplate || selectedElementIds.length === 0) return

    copy(0)
    onElementsDeleted()
  }, [currentTemplate, selectedElementIds, copy, onElementsDeleted])

  return {
    handleCopyElement,
    handlePasteElement,
    handleCutElement,
    hasCopiedElement: copiedElements.length > 0,
  }
}

export default useClipboard
